import { DynamicForm } from "@shared/components/DynamicForm";
import { GenericTable } from "@shared/components/GenericTable";
import { FormField, FormService } from "@shared/services/FormServices";
import { TabsContent } from "@shared/ui/tabs";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { PAGE_SIZE } from "@shared/utils/constants";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { useAreaList, useDivisionList, usePremisesForBillCycle, useSubAreaList, useZoneList } from "../hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { PaginatedResponsePremise } from "../types";

interface PremiseTableProps {
  disableSelection: boolean;
  selectedCategories: string[],
  selectedSubCategories: string[],
  selectedPremises: string[];
  onSelectionChange: (selectedIds: string[]) => void;
  onPremiseListChange?: (premiseList: PaginatedResponsePremise) => void;
}

export const PremiseTable: React.FC<PremiseTableProps> = ({
  disableSelection,
  selectedCategories,
  selectedSubCategories,
  selectedPremises,
  onSelectionChange,
  onPremiseListChange,
}) => {

  const remoteUtilityId = useRemoteUtilityId();
  
  // ------------------ STATE ---------------------
  const [premisesSearch, setPremisesSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  
  // Separate state for form values (for cascading dropdowns)
  const [formFilterValues, setFormFilterValues] = useState({
    zone: [],
    division: [],
    area: [],
    sub_area: [],
  });
  
  // State for actual query params (only updated on Apply Filters)
  const [queryParams, setQueryParams] = useState({
    page: currentPage,
    search_data: "",
    zone: [],
    division: [],
    area: [],
    sub_area: [],
  });

  // ------------------ HIERARCHY QUERIES ---------------------
  const { data: zoneList = [] } = useZoneList();
  
  // Fetch divisions based on current form values (for cascading)
  const { data: divisionList = [] } = useDivisionList(formFilterValues?.zone?.[0]);

  // Fetch areas based on current form values (for cascading)
  const { data: areaList = [] } = useAreaList(formFilterValues?.division?.[0]);

  // Fetch sub-areas based on current form values (for cascading)
  const { data: subAreaList = [] } = useSubAreaList(formFilterValues?.area?.[0]);

  // ------------------ PREMISE LIST ---------------------
  // This hook only uses queryParams, which is updated ONLY on Apply Filters click
  const {data: premiseList} = usePremisesForBillCycle({
    category: selectedCategories,
    subCategory: selectedSubCategories,
    area: queryParams.area[0],
    division: queryParams.division[0],
    zone: queryParams.zone[0],
    subArea: queryParams.sub_area[0],
    search: premisesSearch,
    page: currentPage,
    limit: PAGE_SIZE,
    remoteUtilityId
  })

  const premiselist = premiseList?.results;
  
  // ------------------ FORM FIELDS ---------------------
  const filterFields: FormField[] = [
    {
      name: "zone",
      label: "Zone",
      type: "multi-select",
      maxSelection: 1,
      placeholder: "Select Zone",
      options: zoneList?.map((item: any) => ({
        value: item.id,
        label: item.name,
      })),
      className: "col-span-1",
    },
    {
      name: "division",
      label: "Division",
      type: "multi-select",
      maxSelection: 1,
      placeholder: "Select Division",
      options: divisionList?.map((item: any) => ({
        value: item.id,
        label: item.name,
      })),
      className: "col-span-1",
      // disabled: !formFilterValues.zone || formFilterValues.zone.length === 0,
    },
    {
      name: "area",
      label: "Area",
      type: "multi-select",
      maxSelection: 1,
      placeholder: "Select Area",
      options: areaList?.map((item: any) => ({
        value: item.id,
        label: item.name,
      })),
      className: "col-span-1",
      // disabled: !formFilterValues.division || formFilterValues.division.length === 0,
    },
    {
      name: "sub_area",
      label: "Sub Area",
      maxSelection: 1,
      type: "multi-select",
      placeholder: "Select Sub Area",
      options: subAreaList?.map((item: any) => ({
        value: item.id,
        label: item.name,
      })),
      className: "col-span-1",
      // disabled: !formFilterValues.area || formFilterValues.area.length === 0,
    },
  ];

  // ------------------ FORM ---------------------
  const form = FormService.createForm({
    fields: filterFields,
    mode: "onChange",
    validateOnMount: false,
  });

  const watchedValues = form.watch();

  type CascadeKey = "zone" | "division" | "area" | "sub_area";

type CascadeConfig = {
  resets: readonly CascadeKey[];
};

const CASCADE_MAP: Record<CascadeKey, CascadeConfig> = {
  zone: { resets: ["division", "area", "sub_area"] },
  division: { resets: ["area", "sub_area"] },
  area: { resets: ["sub_area"] },
  sub_area: { resets: [] },
};

const resetFields = (fields: readonly string[]) => {
  fields.forEach((field) => form.setValue(field as any, []));
};

  // ------------------ RESET DEPENDENT FIELDS ---------------------
  // Watch form changes for cascading dropdowns WITHOUT triggering API calls
  useEffect(() => {
  const subscription = form.watch((value, { name }) => {
    if (!name || !(name in CASCADE_MAP)) return;

    const field = name as CascadeKey;
    const { resets } = CASCADE_MAP[field];

    // reset form fields
    resets.forEach((key) => form.setValue(key, []));

    // update cascading filter state
    setFormFilterValues((prev) => ({
      ...prev,
      [field]: value[field] || [],
      ...Object.fromEntries(resets.map((key) => [key, []])),
    }));
  });

  return () => subscription.unsubscribe();
}, [form]);

  // ------------------ ON FILTER SUBMIT ---------------------
  // This is the ONLY place where queryParams is updated (triggers API call)
  const handleFilterSubmit = () => {
    const values = form.getValues();

    setCurrentPage(1);

    // Update query params only when Apply Filters is clicked
    setQueryParams({
      page: 1,
      search_data: premisesSearch.length > 2 ? premisesSearch : "",
      zone: values.zone || [],
      division: values.division || [],
      area: values.area || [],
      sub_area: values.sub_area || [],
    });
  };

  // ------------------ ON FILTER RESET ---------------------
  const handleFilterReset = () => {
    // Reset form values
    form.reset();
    
    // Reset form filter values
    setFormFilterValues({
      zone: [],
      division: [],
      area: [],
      sub_area: [],
    });
    
    // Reset query params (triggers API call with cleared filters)
    setCurrentPage(1);
    setQueryParams({
      page: 1,
      search_data: premisesSearch.length > 2 ? premisesSearch : "",
      zone: [],
      division: [],
      area: [],
      sub_area: [],
    });
  };

  // ------------------ SEARCH ---------------------
  const handleSearchChange = useCallback((search: string) => {
    setPremisesSearch(search);
    setCurrentPage(1);

    setQueryParams((prev) => ({
      ...prev,
      page: 1,
      search_data: search.length > 2 ? search : "",
    }));
  }, []);

  const handleSearch = useCallback(
    (searchTerm: string) => handleSearchChange(searchTerm),
    [handleSearchChange]
  );

  // ------------------ PAGINATION ---------------------
  const paginationConfig = useMemo(() => {
    if (!premiselist) return undefined;

    const totalItems = premiseList.count || 0;
    const totalPages = Math.ceil(totalItems / PAGE_SIZE);

    return {
      currentPage,
      totalPages,
      totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem: (currentPage - 1) * PAGE_SIZE + 1,
      endItem: Math.min(currentPage * PAGE_SIZE, totalItems),
    };
  }, [premiselist, currentPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);

    setQueryParams((prev) => ({
      ...prev,
      page,
    }));
  };

  // ------------------ TABLE COLUMNS ---------------------
  const premisesColumns = [
    { key: "name", header: "Premise", sortable: true },
    { key: "configurationCode", header: "Code", sortable: true },
    {
      key: "zone",
      header: "Zone",
      render: (_, row) => row.hierarchyNames?.zone,
    },
    {
      key: "division",
      header: "Division",
      render: (_, row) => row.hierarchyNames?.division,
    },
    {
      key: "area",
      header: "Area",
      render: (_, row) => row.hierarchyNames?.area,
    },
    {
      key: "subArea",
      header: "Sub Area",
      render: (_, row) => row.hierarchyNames?.subArea,
    },
    {
      key: "consumerCount",
      header: "Consumer Count",
      sortable: true,
      render: (value: number) => value?.toLocaleString(),
    },
    {
      key: "meterCount",
      header: "Meter Count",
      sortable: true,
      render: (value: number) => value?.toLocaleString(),
    },
  ];

  // ------------------ EMIT PREMISE LIST UPWARDS ---------------------
  useEffect(() => {
    if (onPremiseListChange && premiseList) {
      onPremiseListChange(premiseList);
    }
  }, [premiseList]);

  // ------------------ FILTER PANEL ---------------------
  const filters = (
    <div>
      <DynamicForm
        fields={filterFields}
        form={form}
        onSubmit={handleFilterSubmit}
        onReset={handleFilterReset}
        gridColumns={3}
        config={{
          layout: "vertical",
          showSubmitButton: true,
          showResetButton: true,
          submitButtonText: "Apply Filters",
          resetButtonText: "Clear",
          classes: {
            ...FIELD_CLASSES,
            buttonContainer: "flex justify-end gap-3",
          },
        }}
      />
    </div>
  );

  // ------------------ RETURN ---------------------
  return (
    <GenericTable
      data={premiselist ?? []}
      columns={premisesColumns}
      rowKey="code"
      selectable={!disableSelection}
      selectedRows={selectedPremises}
      onSelectionChange={onSelectionChange}
      searchPlaceholder="Search premises..."
      onSearch={handleSearch}
      emptyMessage="No premises match your search criteria"
      onPageChange={handlePageChange}
      pagination={paginationConfig}
      filterPanel={filters}
    />
  );
};